import CopyButton from '@components/Buttons/CopyButton'
import Filters from '@components/Filters/Filters.jsx'
import Pagination from '@components/Pagination/Pagination'
import { STORAGE_KEY } from '@utils/constants/storage'
import { sortValues } from '@utils/sort'
import { toStorageSlug } from '@utils/storage'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Skeleton } from 'primereact/skeleton'
import { createElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'

// Longer arrays are truncated to keep table rows compact, the rest is shown on hover
const MAX_DISPLAYED_ARRAY_VALUES = 3

/**
 * Renders an icon link styled for list row actions, used internally by the `Action*` helpers.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className=null] - CSS class applied to the link.
 * @param {string} [props.iconClassName=null] - CSS class applied to the inner icon.
 * @param {string} [props.to=null] - Link target URL.
 * @param {Boolean} [props.blank=false] - Whether to open the link in a new tab.
 * @returns {JSX.Element}
 */
const ActionLink = ({ className = null, iconClassName = null, to = null, blank = false }) => {
  return (
    <Link
      className={className}
      to={to}
      target={blank ? '_blank' : '_self'}
    >
      <i className={iconClassName} />
    </Link>
  )
}

/**
 * Renders a "view details" link/icon for a list row, used as a column action.
 *
 * @param {string} detailUrl - URL opened in a new tab.
 * @returns {JSX.Element}
 */
export const ActionOpenDetail = detailUrl => {
  return (
    <ActionLink
      className="open-detail-button"
      iconClassName="fa-solid fa-arrow-up-right-from-square"
      to={detailUrl}
      blank={true}
    />
  )
}

/**
 * Renders a "delete" link/icon for a list row, used as a column action.
 *
 * @param {string} deleteUrl - URL navigated to when the delete action is clicked.
 * @returns {JSX.Element}
 */
export const ActionDelete = deleteUrl => {
  return (
    <ActionLink
      className="delete-button"
      iconClassName="fa-solid fa-trash-can"
      to={deleteUrl}
      blank={false}
    />
  )
}

/**
 * Renders an "edit" link/icon for a list row, used as a column action.
 *
 * @param {string} editUrl - URL navigated to when the edit action is clicked.
 * @returns {JSX.Element}
 */
export const ActionEdit = editUrl => {
  return (
    <ActionLink
      className="edit-button"
      iconClassName="fa-solid fa-pen"
      to={editUrl}
      blank={false}
    />
  )
}

/**
 * Renders a "download" icon button for a list row, invoking the given callback with the row id.
 *
 * @param {Function} download - Callback invoked with `id` when the button is clicked.
 * @param {*} id - Identifier of the row/item to download.
 * @returns {JSX.Element}
 */
export const ActionDownload = (download, id) => {
  return (
    <div
      className="download-button"
      onClick={e => {
        e.preventDefault()
        download(id)
      }}
    >
      <i className="fa-solid fa-download" />
    </div>
  )
}

/**
 * Renders a generic clickable icon action for a list row, with disabled/loading states.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onClick - Callback invoked with `id` when clicked (ignored while disabled/loading).
 * @param {*} props.id - Identifier passed through to `onClick`.
 * @param {string} props.icon - Icon class name to display (overridden by a spinner icon while loading).
 * @param {string} props.title - Tooltip title attribute for the action.
 * @param {Boolean} [props.disabled=false] - Whether the action is disabled.
 * @param {Boolean} [props.isLoading=false] - Whether the action is in a loading state (also disables it).
 * @returns {JSX.Element}
 */
export const ActionOnClick = ({
  onClick,
  id,
  icon,
  title,
  disabled = false,
  isLoading = false
}) => {
  const displayIcon = isLoading ? 'fa-spin fa-spinner' : icon
  const isDisabled = disabled || isLoading

  return (
    <div
      className="onclick-button"
      title={title}
      style={{
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto'
      }}
      onClick={e => {
        if (!isDisabled) {
          e.preventDefault()
          onClick(id)
        }
      }}
    >
      <i className={`fa-solid ${displayIcon}`} />
    </div>
  )
}

/**
 * Renders the default detail/delete action buttons for a list row.
 *
 * @component
 * @param {Object} props
 * @param {*} props.id - Identifier of the row/item.
 * @param {Object} props.data - Full row data, passed through to the URL builders.
 * @param {Function} [props.getDetailUrl] - Builds the detail URL from `(id, data)`; omits the detail action when not provided.
 * @param {Function} [props.getDeleteUrl] - Builds the delete URL from `(id, data)`; omits the delete action when not provided.
 * @returns {JSX.Element}
 */
const ActionsRow = ({ id, data, getDetailUrl, getDeleteUrl }) => {
  return (
    <div className="p-datatable-action-buttons">
      {getDetailUrl && ActionOpenDetail(getDetailUrl(id, data))}
      {getDeleteUrl && ActionDelete(getDeleteUrl(id, data))}
    </div>
  )
}

/**
 * Table component.
 *
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
const Table = props => {
  const localStorageKey = props.id && toStorageSlug(`${STORAGE_KEY.COLUMNS_VISIBILITY}-${props.id}`)
  const storage = localStorage.getItem(localStorageKey)
  const userVisibleColumns = storage?.split(';') ?? []
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [columns, setColumns] = useState(
    props.columns.map(column => {
      return {
        ...column,
        /**
         * Override the hidden property of column if there is a search param for this column or if the column field is in userVisibleColumns array from local storage.
         * This allow to keep column visibility between page reload and also to toggle column visibility with search params.
         */
        hidden: searchParams.get(column.field)
          ? false
          : !!userVisibleColumns.length
            ? userVisibleColumns.includes(column.field)
              ? false
              : true
            : column.hidden
      }
    })
  )

  const defaultColumns = useMemo(
    () => props.columns.filter(column => !column.hidden).map(column => column.field),
    [props.columns]
  )

  const visibleColumns = useMemo(
    () => columns.filter(column => !column.hidden).map(column => column.field),
    [columns]
  )

  const columnsHasDiffWithDefault = useMemo(
    () =>
      defaultColumns.length !== visibleColumns.length ||
      !!defaultColumns.reduce(
        (accumulator, field) =>
          visibleColumns.includes(field) ? accumulator : [...accumulator, field],
        []
      ).length,
    [defaultColumns, visibleColumns]
  )

  /**
   * Renders the default detail/delete action buttons for a table row.
   *
   * @param {Object} row - Row data for the current line.
   * @returns {JSX.Element}
   */
  const actionsRow = row => {
    const id = row[props.dataKey || 'id']

    return (
      <ActionsRow
        id={id}
        data={row}
        getDetailUrl={props.getDetailUrl}
        getDeleteUrl={props.getDeleteUrl}
      />
    )
  }

  /**
   * Renders a cell value, optionally as a link and/or with a copy-to-clipboard button.
   *
   * @component
   * @param {Object} props
   * @param {*} props.value - Value to display.
   * @param {Boolean} [props.copyEnabled=false] - Whether to render a copy-to-clipboard button next to the value.
   * @param {string|boolean} [props.url=false] - URL to wrap the value in a link; falsy renders plain text.
   * @returns {JSX.Element}
   */
  const FormattedValue = ({ value, copyEnabled = false, url = false }) => (
    <span>
      {url ? <a href={url}>{value}</a> : value}
      {copyEnabled && <CopyButton value={value} />}
    </span>
  )

  /**
   * Resolves a dotted path (e.g. `"a.b.c"`) against an object.
   *
   * @param {Object} obj - Object to read the value from.
   * @param {string} path - Dotted path to resolve.
   * @returns {*} Value found at the path, or a falsy value if any segment is missing.
   */
  const getValueByPath = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj)

  /**
   * Default cell body renderer for a table column, applying `column.processValue`/`column.link` and
   * truncating long array values.
   *
   * @param {Object} rowData - Row data for the current line.
   * @param {Object} column - Column definition (`field`, `processValue`, `link`, `disableCopyButton`, ...).
   * @returns {JSX.Element}
   */
  const defaultBody = (rowData, column) => {
    if (typeof rowData !== 'undefined') {
      const bodyValue =
        column && column.processValue
          ? column.processValue(getValueByPath(rowData, column.field))
          : getValueByPath(rowData, column.field)
      const linkUrl = column && column.link ? column.link(rowData) : null

      return (
        <div className="td-body">
          {bodyValue && (
            <>
              {column.disableCopyButton}

              {Array.isArray(bodyValue) ? (
                <>
                  {bodyValue.slice(0, MAX_DISPLAYED_ARRAY_VALUES).map((value, index) => (
                    <FormattedValue
                      key={index}
                      value={value}
                      url={linkUrl}
                      copyEnabled={!column.disableCopyButton}
                    />
                  ))}
                  {bodyValue.length > MAX_DISPLAYED_ARRAY_VALUES && (
                    <span title={bodyValue.slice(MAX_DISPLAYED_ARRAY_VALUES).join(', ')}>
                      +{bodyValue.length - MAX_DISPLAYED_ARRAY_VALUES}
                    </span>
                  )}
                </>
              ) : (
                <FormattedValue
                  value={bodyValue}
                  url={linkUrl}
                  copyEnabled={!column.disableCopyButton}
                />
              )}
            </>
          )}
        </div>
      )
    }
    return <div />
  }

  /**
   * Toggles the hidden state of the column matching the given field.
   *
   * @param {string} field - Field name of the column to toggle.
   * @returns {void}
   */
  const toggleColumn = field => {
    setColumns(
      columns.map(column =>
        column.field === field ? { ...column, hidden: !column.hidden } : column
      )
    )
  }

  /**
   * Renders the table header's column-visibility toggle and item counter.
   *
   * @returns {JSX.Element}
   */
  const actionsHeader = () => {
    const counter = props.hasOwnProperty('count') ? props.count : props.value && props.value.length
    const op = useRef(null)
    /**
     * Opens/closes the column-visibility overlay panel.
     *
     * @param {Event} [e] - Originating event; its default action is prevented when present.
     * @returns {void}
     */
    const toggle = e => {
      e?.preventDefault()
      op.current?.toggle(e)
    }

    return (
      <div className="p-datatable-info-header">
        {!!counter && (
          <div className="p-datatable-info-header-items-number">
            <span>{counter}</span>
          </div>
        )}
        <button
          className="p-datatable-info-header-select-column"
          onClick={toggle}
        >
          <i className="fa-solid fa-ellipsis-vertical" />
        </button>
        <OverlayPanel
          ref={op}
          unstyled={true}
        >
          <div className="custom-header-dropdown-checkbox">
            {columns
              // Do not display column with hideToggle set to true
              .filter(column => !column.hideToggle)
              .map(column => (
                <label
                  key={column.field}
                  className="custom-header-dropdown-checkbox-item"
                >
                  <Checkbox
                    name={`checkbox-${column.field}`}
                    defaultChecked={false}
                    checked={!column.hidden}
                    onChange={() => toggleColumn(column.field)}
                    // always keep the first column display
                    disabled={
                      columns.filter(column => !column.hidden).length === 1 && !column.hidden
                    }
                  />
                  <span>{column.header}</span>
                </label>
              ))}
          </div>
        </OverlayPanel>
      </div>
    )
  }

  /**
   * Stops the row-select event from propagating to the row click handler.
   *
   * @param {Object} e - PrimeReact DataTable row-select event.
   * @returns {void}
   */
  const onRowSelect = e => e.originalEvent.stopPropagation()

  /**
   * Stops the row-unselect event from propagating to the row click handler.
   *
   * @param {Object} e - PrimeReact DataTable row-unselect event.
   * @returns {void}
   */
  const onRowUnselect = e => e.originalEvent.stopPropagation()

  /**
   * Forwards a row click to `props.onRowClick` with the row id and data.
   *
   * @param {Object} e - PrimeReact DataTable row-click event.
   * @returns {void}
   */
  const onRowClick = e => {
    props.onRowClick && props.onRowClick(e.data.id, e.data)
  }

  // I had to disable sorting in datable to avoid weird behavior with the custom header
  // So, for list who don't have sort on backed side, I'll do the sort here
  /**
   * Returns the table values sorted by `sortField`/`sortOrder` when sorting isn't handled by the backend.
   *
   * @returns {Array} Sorted (or original) row values.
   */
  const sortedValues = () => {
    if (props.sortOrder && props.sortField) {
      return sortValues(props.value, props.sortField, props.sortOrder)
    }
    return props.value
  }

  /**
   * Renders the actions column body for a table row: a skeleton while loading, a custom
   * `props.actionsRow` renderer when provided, or the default `actionsRow`.
   *
   * @param {Object} row - Row data for the current line.
   * @returns {JSX.Element}
   */
  const renderActionsRow = row => {
    if (props.loading) {
      return <Skeleton />
    } else if (props.actionsRow) {
      return props.actionsRow(row)
    } else {
      return actionsRow(row)
    }
  }

  useEffect(() => {
    /**
     * If there is diffs, we save visible columns in local storage,
     * else we remove it from local storage to use default columns config on next load
     */
    if (localStorageKey) {
      if (columnsHasDiffWithDefault) {
        localStorage.setItem(localStorageKey, visibleColumns.join(';'))
      } else {
        localStorage.removeItem(localStorageKey)
      }
    }
  }, [columnsHasDiffWithDefault, visibleColumns])

  return (
    <DataTable
      dataKey={props.dataKey || 'id'}
      selectionMode={props.handleSelection ? 'checkbox' : props.selectionMode}
      onSelectionChange={e => props.setSelectedItems(e.value)}
      // if not backend sort, we sort values
      value={props.backendSort ? props.value : sortedValues()}
      selection={props.selection}
      rowGroupMode={props.rowGroupMode}
      groupRowsBy={props.groupRowsBy}
      rowGroupHeaderTemplate={props.rowGroupHeaderTemplate}
      rowGroupFooterTemplate={props.rowGroupFooterTemplate}
      expandableRowGroups={props.expandableRowGroups}
      expandedRows={props.expandedRows}
      filters={props.filters}
      rowClassName={props.rowClassName}
      cellClassName={props.cellClassName}
      onValueChange={props.onValueChange}
      onRowClick={onRowClick}
      onRowSelect={onRowSelect}
      onRowUnselect={onRowUnselect}
      onRowToggle={props.onRowToggle}
      emptyMessage={props.loading ? t('common.loading') : t('common.no_results_found')}
      scrollable={props.scrollable}
      scrollHeight={props.scrollHeight}
      className={[props.className, props.onRowClick && 'p-datatable-clickable-row']}
      isDataSelectable={props.isDataSelectable}
      cellMemo={props.cellMemo ?? false}
      cellMemoProps={props.cellMemoProps ?? []}
    >
      {props.handleSelection && <Column selectionMode="multiple" />}
      {columns.map(column => {
        return (
          <Column
            {...column}
            sortable={false}
            key={column.field}
            header={
              <Filters
                field={column.field}
                sortField={column.sortField}
                header={column.header}
                sortable={column.sortable}
                filterable={column.filterable}
                options={column.options}
                type={column.type}
                currentSortField={props.sortField}
                currentSortOrder={props.sortOrder}
                setSortField={props.setSortField}
                setSortOrder={props.setSortOrder}
                orderingCallback={props.orderingCallback}
                showFilters={props.showFilters}
                filters={props.filters}
                setFilters={props.setFilters}
              />
            }
            body={
              props.loading ? (
                <Skeleton />
              ) : (
                column.body || (rowData => defaultBody(rowData, column))
              )
            }
          />
        )
      })}
      {!props.hideActions && (
        <Column
          className="p-datatable-action"
          headerClassName="p-datatable-action"
          alignHeader="right"
          body={renderActionsRow}
          header={actionsHeader}
        />
      )}
    </DataTable>
  )
}

/**
 * Main component for list layout.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.bulkDeleteAction
 * @param {string} props.bulkDeleteUrl
 * @param {Array} props.columns
 * @param {Function} props.cellClassName
 * @param {number} props.count
 * @param {string} props.dataKey
 * @param {string} [props.id] - DOM id applied to the layout's root element.
 * @param {string} props.groupRowsBy
 * @param {Function} props.getDeleteUrl
 * @param {Function} props.getDetailUrl
 * @param {boolean} props.expandableRowGroups
 * @param {Array} props.expandedRows
 * @param {Object} props.filters
 * @param {Function} props.handleSelection
 * @param {number} props.limit
 * @param {boolean} props.loading
 * @param {boolean} props.loadingNext
 * @param {Function} props.loadMore
 * @param {boolean} props.next
 * @param {number} props.offset
 * @param {Function} props.onRowClick
 * @param {Function} props.onRowToggle
 * @param {Function} props.onValueChange
 * @param {Function} props.orderingCallback
 * @param {Function} props.rowClassName
 * @param {Function} props.rowGroupFooterTemplate
 * @param {Function} props.rowGroupHeaderTemplate
 * @param {string} props.rowGroupMode
 * @param {boolean} props.processingBulkDeleteAction
 * @param {Array} props.selection
 * @param {string} props.selectionMode
 * @param {Function} props.setFilters
 * @param {Function} props.setLimit
 * @param {Function} props.setOffset
 * @param {Function} props.setSortField
 * @param {Function} props.setSortOrder
 * @param {boolean} props.showFilters
 * @param {string} props.sortField
 * @param {number} props.sortOrder
 * @param {Array} props.value
 * @returns {JSX.Element}
 */
export default function ListLayout(props) {
  /**
   * Reads the current window width.
   *
   * @returns {number} Current `window.innerWidth`.
   */
  const getWidth = () => window.innerWidth
  const { limit, offset, setLimit, setOffset, count, orderingCallback } = props
  const [selectedItems, setSelectedItems] = useState([])
  const [screenWidth, setScreenWidth] = useState(getWidth())
  const { t } = useTranslation()
  const hasBulkAction = props.bulkDeleteAction || props.bulkDeleteUrl

  /**
   * Renders the bulk-delete button, showing a spinner icon while the action is processing.
   *
   * @component
   * @returns {JSX.Element}
   */
  const BulkDeleteButton = () => (
    <Button
      rounded
      onClick={props.bulkDeleteAction}
      icon={
        props.processingBulkDeleteAction ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-trash-can'
      }
      label={t('common.delete_selected')}
      disabled={props.processingBulkDeleteAction}
      severity="danger"
    />
  )

  /**
   * Wraps its children in a link to `props.bulkDeleteUrl` when set, defaulting the children to a
   * `BulkDeleteButton`; otherwise renders the children (or default button) unwrapped.
   *
   * @component
   * @param {Object} props
   * @param {JSX.Element} [props.children] - Content to render, defaults to a `BulkDeleteButton`.
   * @returns {JSX.Element}
   */
  const BulkDeleteLink = ({ children = createElement(BulkDeleteButton) }) =>
    props.bulkDeleteUrl ? <Link to={props.bulkDeleteUrl}>{children}</Link> : children

  const listLayoutClassnames = useMemo(
    () =>
      ['list-layout-container', props.columns.length === 1 && 'has-single-column'].filter(Boolean),
    [props.columns]
  )

  useEffect(() => {
    /**
     * Updates the tracked screen width state from the current window width.
     *
     * @returns {void}
     */
    const updateWidth = () => {
      setScreenWidth(getWidth())
    }

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [screenWidth])

  useEffect(() => {
    selectedItems && props.handleSelection && props.handleSelection(selectedItems)
  }, [selectedItems])

  useEffect(() => {
    setSelectedItems(props.selection ?? [])
  }, [props.value])

  return (
    <div
      id={props.id}
      className={listLayoutClassnames.join(' ')}
    >
      <Table
        {...props}
        selection={selectedItems}
        setSelectedItems={setSelectedItems}
        orderingCallback={orderingCallback}
      />

      {!selectedItems.length && props.loadMore && props.next && (
        <div className="list-layout-more">
          <Button
            rounded
            icon={
              props.loadingNext ? 'fa-solid fa-rotate-right fa-spin' : 'fa-solid fa-rotate-right'
            }
            label={t('common.load_more')}
            severity="secondary"
            onClick={() => props.loadMore()}
          />
        </div>
      )}

      {props.handleSelection && hasBulkAction && !!selectedItems.length && (
        <div className="list-bulk-actions">
          <BulkDeleteLink />
        </div>
      )}

      <Pagination {...{ offset, limit, setOffset, setLimit, count }} />
    </div>
  )
}
